/** Who's signed in, which business, their role and what the UI may offer them. The Worker still decides. */
import { CLIENT_PERMISSIONS, ROLE_TABS, type ClientAction, type auth } from '@ff/contracts';
import type { z } from 'zod';
import { ApiError, call, setTenant } from './api.ts';
import { clearMemoryCache } from './data.ts';
import { useDatabaseFor, wipeUserData } from './idb.ts';
import { navigate } from './router.ts';
import { createStore, useStore } from './store.ts';

export type Me = z.infer<typeof auth.Me>;
export type Membership = Me['memberships'][number];

export type SessionState =
  | { status: 'loading' }
  | { status: 'signed_out' }
  | { status: 'offline_unknown' }
  | { status: 'no_access'; me: Me }
  | { status: 'ready'; me: Me; membership: Membership };

export const session = createStore<SessionState>({ status: 'loading' });

const TENANT_KEY = 'ff-tenant';
const ME_KEY = 'ff-me';

function remember(key: string, value: string | null) {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    /* private mode */
  }
}
function recall(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function choose(me: Me, prefer: string | null): SessionState {
  const m = me.memberships.find((x) => x.tenantId === prefer) ?? me.memberships[0];
  if (!m) return { status: 'no_access', me };
  setTenant(m.tenantId);
  useDatabaseFor(me.user.id);
  remember(TENANT_KEY, m.tenantId);
  return { status: 'ready', me, membership: m };
}

export async function boot(): Promise<void> {
  try {
    const me = await call('me');
    // Kept so the app opens offline (docs/52 §5 offline: reading works from the cache).
    remember(ME_KEY, JSON.stringify(me));
    session.set(choose(me, recall(TENANT_KEY)));
  } catch (e) {
    if (e instanceof ApiError && e.offline) {
      const cached = recall(ME_KEY);
      if (cached) {
        try {
          session.set(choose(JSON.parse(cached) as Me, recall(TENANT_KEY)));
          return;
        } catch {
          /* fall through */
        }
      }
      session.set({ status: 'offline_unknown' });
      return;
    }
    session.set({ status: 'signed_out' });
  }
}

export function switchTenant(tenantId: string): void {
  const s = session.get();
  if (s.status !== 'ready') return;
  clearMemoryCache();
  session.set(choose(s.me, tenantId));
  navigate('/');
}

export async function signOut(): Promise<void> {
  try {
    await call('authSignOut');
  } catch {
    /* signing out locally still happens */
  }
  await wipeUserData();
  clearMemoryCache();
  remember(ME_KEY, null);
  setTenant(null);
  session.set({ status: 'signed_out' });
  navigate('/signin', { replace: true });
}

export function useSession(): SessionState {
  return useStore(session);
}

/** The ready session, for screens that only render once signed in. */
export function useReady(): Extract<SessionState, { status: 'ready' }> {
  const s = useStore(session);
  if (s.status !== 'ready') throw new Error('useReady outside a ready session');
  return s;
}

/** Whether the UI should offer an action. `own` rows are offered (the Worker scopes them to the user's own). */
export function can(m: Membership, action: ClientAction): boolean {
  const g = CLIENT_PERMISSIONS[action][m.role];
  if (g === 'yes' || g === 'own') return true;
  if (g === 'approver') return m.canApprove;
  return false;
}

export function tabsFor(m: Membership) {
  return ROLE_TABS[m.role];
}

export function firstName(me: Me): string | null {
  return me.user.name?.split(/\s+/)[0] ?? null;
}

export function ownerName(me: Me, m: Membership): string {
  return m.role === 'owner' ? (me.user.name ?? '') : '';
}
